/* eslint-disable jsx-a11y/no-autofocus -- the password is the first field */
import React, { useState } from 'react';
import { ApolloClient, useApolloClient, useQuery } from '@apollo/client';
import { Text } from 'rebass';
import Button from '@/components/Button/Button';
import { Input, Label } from '@/components/Form';
import Form from '@/components/Form/Form';
import FormColumn from '@/components/Form/FormColumn';
import FormRow from '@/components/Form/FormRow';
import Modal, { ModalBody, ModalFooter } from '@/components/Modal';
import useNotify from '@/hooks/useNotify';
import useSessionInfo from '@/hooks/useSessionInfo';
import { authClient } from '@/services/auth';
import type {
  Challenge,
  MfaMethod,
  StartVerification,
} from '@ecosconnect/core/src/auth/types';
import ErrorText from '@/components/Form/ErrorText';
import { formatPhone } from './formatPhone';
import {
  REMOVE_PHONE,
  SECURITY_SETTINGS,
  SET_TWO_FACTOR_METHOD,
} from './Security.queries';
import TwoFactorMethodPicker from './TwoFactorMethodPicker';
import { VerificationCodeStep } from './VerificationCodeFields';

type Details = {
  method: MfaMethod;
  phone: string;
  email: string;
  password: string;
  emailVerified: boolean;
};

type Flow = {
  title: string;
  intro: string | ((details: Details) => string);
  confirmLabel: string;
  success: string;
  field?: 'method' | 'phone' | 'email';
  via?: MfaMethod;
  input: (details: Details) => StartVerification | undefined;
  apply?: {
    label: string;
    run: (client: ApolloClient<object>, details: Details) => Promise<unknown>;
  };
};

const verifyEmail = ({ email, password }: Details): StartVerification => ({
  purpose: 'set-email',
  email,
  password,
});

const setTwoFactorMethod = (client: ApolloClient<object>, method: MfaMethod) =>
  client.mutate({ mutation: SET_TWO_FACTOR_METHOD, variables: { method } });

const FLOWS = {
  turnOnTwoFactor: {
    title: 'Turn on two-factor authentication',
    intro: ({ method, emailVerified }) =>
      method === 'email' && !emailVerified
        ? 'Choose where we send a code each time you sign in. We’ll email a code to confirm your email first.'
        : 'Choose where we send a code each time you sign in.',
    confirmLabel: 'Verify and turn on',
    success: 'Two-factor authentication turned on',
    field: 'method',
    input: (details) =>
      details.method === 'email' && !details.emailVerified
        ? verifyEmail(details)
        : undefined,
    apply: {
      label: 'Turn on',
      run: (client, { method }) => setTwoFactorMethod(client, method),
    },
  },
  useEmailForTwoFactor: {
    title: 'Send codes by email',
    intro: 'We’ll email a code to confirm your email first.',
    confirmLabel: 'Verify and switch',
    success: 'Two-factor method updated',
    via: 'email',
    input: verifyEmail,
    apply: {
      label: 'Switch',
      run: (client) => setTwoFactorMethod(client, 'email'),
    },
  },
  setPhone: {
    title: 'Mobile phone',
    intro: 'We’ll text a code to confirm this number.',
    confirmLabel: 'Verify and save',
    success: 'Mobile phone saved',
    field: 'phone',
    via: 'sms',
    input: ({ phone, password }) =>
      phone ? { purpose: 'set-phone', phone, password } : undefined,
  },
  setEmail: {
    title: 'Change email',
    intro: 'We’ll email a code to confirm this address.',
    confirmLabel: 'Verify and save',
    success: 'Email updated',
    field: 'email',
    via: 'email',
    input: (details) =>
      details.email.trim() ? verifyEmail(details) : undefined,
  },
  removePhone: {
    title: 'Remove mobile phone',
    intro:
      'Two-factor codes will go to your email instead. We’ll email a code to confirm your email first.',
    confirmLabel: 'Verify and remove',
    success: 'Mobile phone removed',
    via: 'email',
    input: verifyEmail,
    apply: {
      label: 'Remove phone',
      run: (client, { password }) =>
        client.mutate({ mutation: REMOVE_PHONE, variables: { password } }),
    },
  },
  turnOffTwoFactor: {
    title: 'Turn off two-factor authentication',
    intro:
      'You will sign in with only your password. We’ll send a code to confirm.',
    confirmLabel: 'Verify and turn off',
    success: 'Two-factor authentication turned off',
    input: ({ password }) => ({ purpose: 'disable-mfa', password }),
  },
} satisfies Record<string, Flow>;

export type VerificationFlow = keyof typeof FLOWS;

const SEND_LABELS: Record<MfaMethod, string> = {
  sms: 'Text me a code',
  email: 'Email me a code',
};

type Step =
  | { kind: 'details'; message: string }
  | {
      kind: 'code';
      purpose: StartVerification['purpose'];
      challenge: Challenge;
    };

type CodeVerificationModalProps = {
  flow: VerificationFlow;
  onClose: () => void;
};

const CodeVerificationModal: React.FC<CodeVerificationModalProps> = ({
  flow: flowName,
  onClose,
}) => {
  const flow: Flow = FLOWS[flowName];
  const client = useApolloClient();
  const { showSuccess } = useNotify();
  const currentEmail = useSessionInfo().session?.userProfile?.user?.email ?? '';
  const { data } = useQuery(SECURITY_SETTINGS);
  const security = data?.securitySettings;
  const [step, setStep] = useState<Step>({ kind: 'details', message: '' });
  const [password, setPassword] = useState('');
  const [chosenMethod, setMethod] = useState<MfaMethod>();
  const [typedPhone, setPhone] = useState<string>();
  const [typedEmail, setEmail] = useState<string>();
  const [pending, setPending] = useState(false);

  const method =
    flow.via ??
    chosenMethod ??
    security?.twoFactorMethod ??
    (security?.phone ? 'sms' : 'email');
  const suggested =
    !security?.phone &&
    data?.session.userProfile.phones.find(
      (phone) => phone.subType === 'CELL' && phone.value.trim()
    )?.value;
  const details: Details = {
    method,
    phone: typedPhone ?? (suggested ? formatPhone(suggested) : ''),
    email: typedEmail ?? currentEmail,
    password,
    emailVerified: !!security?.emailVerified,
  };
  const input = flow.input(details);
  const direct = !input && flow.apply;
  const canSend = !pending && (direct ? !!security : !!password && !!input);

  const clearError = () => setStep({ kind: 'details', message: '' });

  const complete = async (purpose?: StartVerification['purpose']) => {
    const failure = await flow.apply?.run(client, details).then(
      () => undefined,
      (err: Error) => err
    );
    await client.refetchQueries({
      include:
        purpose === 'set-email'
          ? [SECURITY_SETTINGS, 'GetMe']
          : [SECURITY_SETTINGS],
    });
    if (failure) {
      setStep({ kind: 'details', message: failure.message });
      return;
    }
    showSuccess(flow.success);
    onClose();
  };

  const send = async () => {
    if (!canSend) return;
    setPending(true);
    clearError();
    try {
      if (input) {
        setStep({
          kind: 'code',
          purpose: input.purpose,
          challenge: await authClient.start(input),
        });
      } else {
        await complete();
      }
    } catch (err) {
      setStep({ kind: 'details', message: (err as Error).message });
    } finally {
      setPending(false);
    }
  };

  return (
    <Modal title={flow.title} onClose={onClose}>
      {step.kind === 'code' ? (
        <VerificationCodeStep
          id="verification-code"
          purpose={step.purpose}
          challenge={step.challenge}
          confirmLabel={flow.confirmLabel}
          onVerified={() => complete(step.purpose)}
          onBack={(message) => setStep({ kind: 'details', message })}
        />
      ) : (
        <>
          <ModalBody>
            <Form onSubmit={send}>
              <Text variant="body2" color="fieldBorderHover">
                {typeof flow.intro === 'string'
                  ? flow.intro
                  : flow.intro(details)}
              </Text>
              {flow.field === 'method' && (
                <FormRow>
                  <FormColumn>
                    <TwoFactorMethodPicker
                      value={method}
                      hasPhone={!!security?.phone}
                      disabled={pending}
                      align="flex-start"
                      onChange={(value) => {
                        setMethod(value);
                        clearError();
                      }}
                    />
                  </FormColumn>
                </FormRow>
              )}
              {flow.field === 'email' && (
                <FormRow>
                  <FormColumn>
                    <Label htmlFor="verification-email">Email</Label>
                    <Input
                      id="verification-email"
                      type="email"
                      autoFocus
                      autoComplete="email"
                      width="100%"
                      value={details.email}
                      disabled={pending}
                      onChange={(event) => {
                        setEmail(event.target.value);
                        clearError();
                      }}
                    />
                  </FormColumn>
                </FormRow>
              )}
              {!direct && (
                <FormRow>
                  <FormColumn>
                    <Label htmlFor="verification-password">
                      Current password
                    </Label>
                    <Input
                      id="verification-password"
                      type="password"
                      autoFocus={flow.field !== 'email'}
                      autoComplete="current-password"
                      width="100%"
                      value={password}
                      disabled={pending}
                      onChange={(event) => {
                        setPassword(event.target.value);
                        clearError();
                      }}
                    />
                  </FormColumn>
                </FormRow>
              )}
              {flow.field === 'phone' && (
                <FormRow>
                  <FormColumn>
                    <Label htmlFor="verification-phone">Mobile phone</Label>
                    <Input
                      id="verification-phone"
                      type="tel"
                      autoComplete="tel"
                      width="100%"
                      value={details.phone}
                      placeholder="555-555-1234"
                      disabled={pending}
                      onChange={(event) => {
                        setPhone(event.target.value);
                        clearError();
                      }}
                    />
                  </FormColumn>
                </FormRow>
              )}
              <ErrorText message={step.message} />
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button onClick={send} disabled={!canSend}>
              {direct
                ? pending
                  ? 'Saving...'
                  : direct.label
                : pending
                  ? 'Sending...'
                  : SEND_LABELS[method]}
            </Button>
            <Button variant="outline" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

export default CodeVerificationModal;
